import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useSnackbar } from 'notistack';
import {
	setAlertActionCreator,
	setPageHeaderTextActionCreator,
	setTableStateActionCreator,
} from 'plugins/store/reducers/common.reducer';
import { can } from 'utils/permissions/permissionsChecker';
import permissionsMap from 'utils/permissions/permissionsMap';

import { setNumberRowsPerPageActionCreator } from 'plugins/store/reducers/settings.reducer';
import { deleteCustomerRegistrations } from 'services/customer-registration.service';
import layoutNames from 'utils/enums/layout-names.enum';

// @material-ui/icons
import PeopleOutline from '@material-ui/icons/PeopleOutline';

// core components
import GridContainer from 'components/Grid/GridContainer';
import GridItem from 'components/Grid/GridItem';
import Card from 'components/Card/Card';
import CardBody from 'components/Card/CardBody';
import CardHeader from 'components/Card/CardHeader';
import CardIcon from 'components/Card/CardIcon';
import ReactTable from 'components/ReactTable/ReactTable';
import SnackMessage from 'components/Snackbar/SnackMessage';
import CustomPhoneLabel from 'components/CustomPhoneLabel/CustomPhoneLabel';
import { getCustomerRegistrations } from 'services/customer-registration.service';
import { mapCustomerRegistrationsResult } from 'mappings/customerRegistrationMappings';

const CustomerRegistrations = ({
	setAlert,
	setPageHeaderText,
	rowsPerPage,
	setRowsPerPage,
	defaultSearchQuery,
	defaultSortBy,
	defaultPageIndex,
	setTableState,
}) => {

	const columns = React.useMemo(
		() => [
			{
				Header: 'Name',
				accessor: 'name',
			},
			{
				Header: 'Phone Number',
				accessor: 'phoneNumber',
				Cell: ({ value }) => (value ? (<CustomPhoneLabel value={value} />) : null),
			},
		],
		[]
	);

	const [data, setData] = React.useState([]);
	const [totalRecordsCount, setTotalRecordsCount] = React.useState(0);
	const [pageCount, setPageCount] = React.useState(0);
	const [loading, setLoading] = React.useState(true);
	const fetchIdRef = React.useRef(0);
	const viewName = 'Customer Registrations';

	// component hook
	React.useEffect(() => {
		setPageHeaderText(viewName);

		return function componentWillUnmount() {
			setPageHeaderText('');
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const { enqueueSnackbar } = useSnackbar();

	const fetchData = React.useCallback(
		async ({ filterQuery, pageIndex, pageSize, sortBy }) => {
			const fetchId = ++fetchIdRef.current;
			setLoading(true);
			if (fetchId === fetchIdRef.current) {
				const result = await getCustomerRegistrations({
					filterQuery,
					pageIndex,
					pageSize,
					sortBy,
				});
				setLoading(false);
				const mappedResult = mapCustomerRegistrationsResult(result);
				setData(mappedResult.data);
				setTotalRecordsCount(mappedResult.total);
				setPageCount(Math.ceil(mappedResult.total / pageSize));
			}
			setRowsPerPage({
				table: 'customerRegistrations',
				rowsPerPage: pageSize
			});
			setTableState({
				table: 'customerRegistrations',
				state: {
					searchQuery: filterQuery,
					sortBy: sortBy,
					page: pageIndex
				}
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	const onDelete = (selection, refreshData) => {
		const idsToDelete = selection.map((item) => {
			return item.original.id;
		});
		const deleteNotifications = {
			type: 'warning',
			title: 'Do you want to remove selected Customer Registration(s)?',
			message: 'This action can not be undone',
			confirmBtnText: 'REMOVE',
			onConfirm: async () => {
				setAlert(null);
				try {
					await deleteCustomerRegistrations(idsToDelete);
					refreshData();
					enqueueSnackbar(
						'Customer Registration(s) successfully removed',
						{
							content: (key, message) => (
								<SnackMessage
									id={key}
									message={message}
									color="success"
								/>
							),
						}
					);
				} catch (e) {
					enqueueSnackbar(e.resolvedErrorMessage, {
						content: (key, message) => (
							<SnackMessage
								id={key}
								message={message}
								color="danger"
							/>
						),
					});
				}
			},
			onCancel: () => {
				setAlert(null);
			},
		};
		setAlert(deleteNotifications);
	};

	return (
		<GridContainer>
			<GridItem xs={12} sm={12} md={12}>
				<Card>
					<CardHeader style={{ position: 'absolute' }} color="rose" icon>
						<CardIcon color="rose">
							<PeopleOutline />
						</CardIcon>
					</CardHeader>
					<CardBody>
						<GridContainer>
							<GridItem xs={12} sm={12} md={12}>
								<ReactTable
									rowsWithSelection={can(permissionsMap.customerRegistration.items.customerRegistrationInformationView.delete)}
									isAllowDelete={can(permissionsMap.customerRegistration.items.customerRegistrationInformationView.delete)}
									columns={columns}
									data={data}
									fetchData={fetchData}
									rowsPerPage={rowsPerPage}
									defaultSearchQuery={defaultSearchQuery}
									defaultSortBy={defaultSortBy}
									defaultPageIndex={defaultPageIndex}
									loading={loading}
									pageCount={pageCount}
									totalRecordsCount={totalRecordsCount}
									detailsPath={
										`${layoutNames.portal}/customer-registrations/details`
									}
									allowDeleteAll
									onDelete={onDelete}
									isPageRootComponent
								></ReactTable>
							</GridItem>
						</GridContainer>
					</CardBody>
				</Card>
			</GridItem>
		</GridContainer>
	);
};

const mapStateToProps = ({
	common: {
		tablesState: {
			customerRegistrations: {
				searchQuery,
				sortBy,
				page,
			} }
	},
	settings: { rowsPerPage } }) => {
	return {
		rowsPerPage: rowsPerPage.customerRegistrations,
		defaultSearchQuery: searchQuery,
		defaultSortBy: sortBy,
		defaultPageIndex: page,
	};
};

const mapDispatchToProps = (dispatch) => ({
	setAlert: bindActionCreators(setAlertActionCreator, dispatch),
	setPageHeaderText: bindActionCreators(
		setPageHeaderTextActionCreator,
		dispatch
	),
	setRowsPerPage: bindActionCreators(
		setNumberRowsPerPageActionCreator,
		dispatch
	),
	setTableState: bindActionCreators(
		setTableStateActionCreator,
		dispatch
	),
});

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(CustomerRegistrations);
